import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaCamera, FaLock, FaGlobe, FaUsers } from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import IdentityTags from './IdentityTags';
import { 
  genderIdentityOptions, 
  sexualOrientationOptions, 
  pronounOptions,
  visibilityOptions 
} from '../../utils/identityHelpers';
import PasswordInput from '../PasswordInput';
import '../../styles/ProfileForm.css';

/**
 * Component for creating and editing user profiles
 */
const ProfileForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel 
}) => {
  const { currentUser } = useUser();
  
  // Initialize form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    location: '',
    avatar: '',
    genderIdentity: [],
    sexualOrientation: '',
    pronouns: '',
    languagesSpoken: [],
    travelStyle: [],
    favoriteDestinations: [],
    isLocalQueer: false,
    anonymousMode: false,
    visibilitySettings: {
      genderIdentity: 'community',
      sexualOrientation: 'community',
      pronouns: 'community',
      location: 'public',
      bio: 'public'
    }
  });
  
  // Form validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form with user data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        password: '',
        confirmPassword: ''
      });
    } else if (currentUser) {
      // Pre-fill with current user data if creating new profile
      setFormData({
        ...currentUser,
        password: '',
        confirmPassword: ''
      });
    }
  }, [initialData, currentUser]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle visibility setting changes
  const handleVisibilityChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      visibilitySettings: {
        ...prev.visibilitySettings,
        [field]: value
      }
    }));
  };
  
  // Handle array field changes (languages, travel style, etc.)
  const handleArrayChange = (field, value) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      
      // Toggle value in array
      if (currentArray.includes(value)) {
        return {
          ...prev,
          [field]: currentArray.filter(item => item !== value)
        };
      } else {
        return {
          ...prev,
          [field]: [...currentArray, value]
        };
      }
    });
  };
  
  // Handle gender identity selection
  const handleGenderSelect = (gender) => {
    handleArrayChange('genderIdentity', gender);
  };
  
  // Handle avatar upload
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // In a real app, this would upload to a server
    // For now, we'll use a local URL
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, avatar: reader.result }));
    };
    reader.readAsDataURL(file);
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.username) newErrors.username = 'Username is required';
    if (!formData.email) newErrors.email = 'Email is required';
    
    // Email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    // Password validation for new users
    if (!initialData) {
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Remove password fields if they're empty (for editing)
    const dataToSubmit = { ...formData };
    if (!dataToSubmit.password) {
      delete dataToSubmit.password;
      delete dataToSubmit.confirmPassword;
    }
    
    // Call the onSubmit callback
    if (onSubmit) {
      onSubmit(dataToSubmit)
        .then(() => {
          setIsSubmitting(false);
        })
        .catch(error => {
          setIsSubmitting(false);
          // Handle submission error
          setErrors(prev => ({ ...prev, form: error.message }));
        });
    }
  };
  
  // Render visibility selector for a field
  const renderVisibilitySelector = (field, label) => {
    const currentVisibility = formData.visibilitySettings[field] || 'public';
    
    return (
      <div className="profile-form__visibility">
        <label>{label} visibility:</label>
        <div className="profile-form__visibility-options">
          {visibilityOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`profile-form__visibility-btn ${currentVisibility === option.value ? 'profile-form__visibility-btn--active' : ''}`}
              onClick={() => handleVisibilityChange(field, option.value)}
              title={option.label}
            >
              {option.value === 'public' && <FaGlobe />}
              {option.value === 'community' && <FaUsers />}
              {option.value === 'private' && <FaLock />}
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <form className="profile-form" onSubmit={handleSubmit}>
      {/* Avatar upload */}
      <div className="profile-form__avatar-section">
        <div className="profile-form__avatar-container">
          <img 
            src={formData.avatar || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} 
            alt="Profile avatar" 
            className="profile-form__avatar" 
          />
          <label className="profile-form__avatar-upload">
            <FaCamera />
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleAvatarChange} 
              className="profile-form__avatar-input" 
            />
          </label>
        </div>
      </div>
      
      {/* Basic Information */}
      <div className="profile-form__section">
        <h3 className="profile-form__section-title">Basic Information</h3>
        
        <div className="profile-form__field">
          <label htmlFor="name" className="profile-form__label">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`profile-form__input ${errors.name ? 'profile-form__input--error' : ''}`}
            placeholder="Your full name"
          />
          {errors.name && <p className="profile-form__error">{errors.name}</p>}
        </div>
        
        <div className="profile-form__field">
          <label htmlFor="username" className="profile-form__label">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={`profile-form__input ${errors.username ? 'profile-form__input--error' : ''}`}
            placeholder="Choose a username"
            disabled={initialData} // Can't change username when editing
          />
          {errors.username && <p className="profile-form__error">{errors.username}</p>}
        </div>
        
        <div className="profile-form__field">
          <label htmlFor="email" className="profile-form__label">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`profile-form__input ${errors.email ? 'profile-form__input--error' : ''}`}
            placeholder="Your email address"
          />
          {errors.email && <p className="profile-form__error">{errors.email}</p>}
        </div>
        
        {/* Only show password fields for new users */}
        {!initialData && (
          <>
            <div className="profile-form__field">
              <label htmlFor="password" className="profile-form__label">Password</label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`profile-form__input ${errors.password ? 'profile-form__input--error' : ''}`}
                placeholder="Create a password"
              />
              {errors.password && <p className="profile-form__error">{errors.password}</p>}
            </div>
            
            <div className="profile-form__field">
              <label htmlFor="confirmPassword" className="profile-form__label">Confirm Password</label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`profile-form__input ${errors.confirmPassword ? 'profile-form__input--error' : ''}`}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && <p className="profile-form__error">{errors.confirmPassword}</p>}
            </div>
          </>
        )}
        
        <div className="profile-form__field">
          <label htmlFor="location" className="profile-form__label">Location</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="profile-form__input"
            placeholder="City, Country"
          />
          {renderVisibilitySelector('location', 'Location')}
        </div>
        
        <div className="profile-form__field">
          <label htmlFor="bio" className="profile-form__label">Bio</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            className="profile-form__textarea"
            placeholder="Tell us about yourself..."
            rows={4}
          />
          {renderVisibilitySelector('bio', 'Bio')}
        </div>
      </div>
      
      {/* Identity Information */}
      <div className="profile-form__section">
        <h3 className="profile-form__section-title">Identity Information</h3>
        
        <div className="profile-form__field">
          <label className="profile-form__label">Gender Identity</label>
          <div className="profile-form__tags-container">
            <IdentityTags 
              tags={genderIdentityOptions} 
              type="gender" 
              selectable={true}
              selected={formData.genderIdentity || []}
              onClick={handleGenderSelect}
            />
          </div>
          {renderVisibilitySelector('genderIdentity', 'Gender identity')}
        </div>
        
        <div className="profile-form__field">
          <label htmlFor="sexualOrientation" className="profile-form__label">Sexual Orientation</label>
          <select
            id="sexualOrientation"
            name="sexualOrientation"
            value={formData.sexualOrientation || ''}
            onChange={handleChange}
            className="profile-form__select"
          >
            <option value="">Select orientation</option>
            {sexualOrientationOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {renderVisibilitySelector('sexualOrientation', 'Sexual orientation')}
        </div>
        
        <div className="profile-form__field">
          <label htmlFor="pronouns" className="profile-form__label">Pronouns</label>
          <select
            id="pronouns"
            name="pronouns"
            value={formData.pronouns || ''}
            onChange={handleChange}
            className="profile-form__select"
          >
            <option value="">Select pronouns</option>
            {pronounOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {renderVisibilitySelector('pronouns', 'Pronouns')}
        </div>
      </div>
      
      {/* Privacy & Safety */}
      <div className="profile-form__section">
        <h3 className="profile-form__section-title">Privacy & Safety</h3>
        
        <div className="profile-form__field profile-form__field--checkbox">
          <input
            type="checkbox"
            id="anonymousMode"
            name="anonymousMode"
            checked={formData.anonymousMode || false}
            onChange={handleChange}
            className="profile-form__checkbox"
          />
          <label htmlFor="anonymousMode" className="profile-form__checkbox-label">
            Enable Anonymous Mode
            <span className="profile-form__help-text">
              When enabled, your name and photo will be hidden from other users
            </span>
          </label>
        </div>
        
        <div className="profile-form__field profile-form__field--checkbox">
          <input
            type="checkbox"
            id="isLocalQueer"
            name="isLocalQueer"
            checked={formData.isLocalQueer || false}
            onChange={handleChange}
            className="profile-form__checkbox"
          />
          <label htmlFor="isLocalQueer" className="profile-form__checkbox-label">
            I am a local LGBTQ+ resident
            <span className="profile-form__help-text">
              This helps travelers connect with local queer community members
            </span>
          </label>
        </div>
      </div>
      
      {/* Form error message */}
      {errors.form && (
        <div className="profile-form__error-message">
          {errors.form}
        </div>
      )}
      
      {/* Form actions */}
      <div className="profile-form__actions">
        <button 
          type="button" 
          className="profile-form__button profile-form__button--secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="profile-form__button profile-form__button--primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Profile' : 'Create Profile'}
        </button>
      </div>
    </form>
  );
};

ProfileForm.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ProfileForm;
